import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Dashboard from './pages/Dashboard';
import MarketsPage from './pages/MarketsPage';
import AITradingPage from './pages/AITradingPage';
import BusinessMarketplace from './pages/BusinessMarketplace';
import DepositPage from './pages/DepositPage';
import WithdrawPage from './pages/WithdrawPage';
import TransactionsPage from './pages/TransactionsPage';
import SettingsPage from './pages/SettingsPage';
import AdminDashboard from './pages/AdminDashboard';
import PortfolioAnalytics from './pages/PortfolioAnalytics';
import BrokerTradingPage from './pages/BrokerTradingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PortfolioPage from './pages/PortfolioPage';
import BusinessOwnerPage from './pages/BusinessOwnerPage';
import LiveMarketsPage from './pages/LiveMarketsPage';
import SocialTradingPage from './pages/SocialTradingPage';
import RockefellerPage from './pages/RockefellerPage';
import { AssistantWidget } from './components/AssistantWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// API instance
export const api = axios.create({
  baseURL: API,
  withCredentials: false  // Use Bearer tokens for auth, not cookies
});

// Add auth header to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth Provider
function AuthProvider({ children }) {
  // Initialize user state from localStorage synchronously to prevent flash
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (storedUser && token) {
        return JSON.parse(storedUser);
      }
    } catch (e) {
      // Invalid stored data
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }
    return null;
  });
  const [loading, setLoading] = useState(true);

  // Global error handler to suppress TradingView script errors
  useEffect(() => {
    const handleError = (event) => {
      // Suppress TradingView widget errors that show "Script error"
      if (event.message === 'Script error.' || 
          (event.message && event.message.includes('tradingview'))) {
        event.preventDefault();
        event.stopPropagation();
        return true;
      }
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (token && user) {
      // Verify token is still valid
      api.get('/auth/me')
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name, referralCode = null) => {
    const res = await api.post('/auth/register', { email, password, name, referral_code: referralCode });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore errors
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// Protected Route
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// Auth Callback for Google OAuth
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      const accessTokenMatch = hash.match(/access_token=([^&]+)/);
      
      if (sessionIdMatch || accessTokenMatch) {
        const sessionId = sessionIdMatch ? sessionIdMatch[1] : null;
        const accessToken = accessTokenMatch ? accessTokenMatch[1] : null;
        
        try {
          const headers = {};
          const body = {};
          
          if (sessionId) {
            headers['X-Session-ID'] = sessionId;
          }
          if (accessToken) {
            body.accessToken = accessToken;
          }
          
          const res = await api.post('/auth/session', body, { headers });
          
          // Store token and user
          if (res.data.token) {
            localStorage.setItem('token', res.data.token);
          }
          localStorage.setItem('user', JSON.stringify(res.data.user || res.data));
          updateUser(res.data.user || res.data);
          
          toast.success('Successfully logged in!');
          navigate('/dashboard', { replace: true });
        } catch (error) {
          console.error('Auth error:', error);
          toast.error(error.response?.data?.detail || 'Authentication failed');
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-400">Authenticating...</p>
      </div>
    </div>
  );
}

// App Router
function AppRouter() {
  const location = useLocation();

  // Check for OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/rockefeller-method" element={<RockefellerPage />} />
      <Route path="/terms" element={<PrivacyPolicyPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/markets" element={<ProtectedRoute><MarketsPage /></ProtectedRoute>} />
      <Route path="/ai-trading" element={<ProtectedRoute><AITradingPage /></ProtectedRoute>} />
      <Route path="/business" element={<ProtectedRoute><BusinessMarketplace /></ProtectedRoute>} />
      <Route path="/deposit" element={<ProtectedRoute><DepositPage /></ProtectedRoute>} />
      <Route path="/deposit/success" element={<ProtectedRoute><DepositPage /></ProtectedRoute>} />
      <Route path="/withdraw" element={<ProtectedRoute><WithdrawPage /></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><PortfolioAnalytics /></ProtectedRoute>} />
      <Route path="/trading" element={<ProtectedRoute><BrokerTradingPage /></ProtectedRoute>} />
      <Route path="/broker" element={<ProtectedRoute><BrokerTradingPage /></ProtectedRoute>} />
      <Route path="/portfolio" element={<ProtectedRoute><PortfolioPage /></ProtectedRoute>} />
      <Route path="/owner" element={<ProtectedRoute><BusinessOwnerPage /></ProtectedRoute>} />
      <Route path="/business-owner" element={<ProtectedRoute><BusinessOwnerPage /></ProtectedRoute>} />
      <Route path="/live-markets" element={<ProtectedRoute><LiveMarketsPage /></ProtectedRoute>} />
      <Route path="/social" element={<ProtectedRoute><SocialTradingPage /></ProtectedRoute>} />
      <Route path="/social-trading" element={<ProtectedRoute><SocialTradingPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <CurrencyWrapper>
              <AppRouter />
              <Toaster position="top-right" richColors />
              <AssistantWidget />
            </CurrencyWrapper>
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

// Wrapper to pass user to CurrencyProvider
function CurrencyWrapper({ children }) {
  const { user } = useAuth();
  return (
    <CurrencyProvider user={user}>
      {children}
    </CurrencyProvider>
  );
}

export default App;
// Frontend rebuild trigger: 1769834776
